// frontend/src/components/SiteLock/SiteLock.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  TextField, DialogActions, Button, Box, CircularProgress,
  Typography, IconButton, Snackbar, Alert, InputAdornment
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import './SiteLock.css';

const fetchWithCredentials = (url, options = {}) => {
  options.credentials = 'include';
  return fetch(url, options);
};

const SiteLock = ({ children }) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const configResponse = await fetchWithCredentials('/api/site-config');
        if (!configResponse.ok) throw new Error('Config fetch failed');
        const config = await configResponse.json();
        setIsEnabled(config.site_lock_enabled);
        setContactEmail(config.contact_email || '');

        if (!config.site_lock_enabled) {
          setIsUnlocked(true);
          setIsLoading(false);
          return;
        }

        const statusResponse = await fetchWithCredentials('/api/site-lock-status');
        if (statusResponse.ok) {
          const status = await statusResponse.json();
          setIsUnlocked(status.unlocked);
        }
      } catch (err) {
        console.error("Error checking site lock status:", err);
        setIsUnlocked(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsUnlocking(true);
    try {
      const response = await fetchWithCredentials('/api/site-unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: password }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.unlocked) {
          setIsUnlocked(true);
          setError('');
        }
      } else {
        let errorMessage = 'Incorrect password. Please try again.';
        try {
          const errorData = await response.json();
          if (errorData.error === 'EXPIRED_PASSWORD') {
            errorMessage = errorData.message || 'This access code has expired.';
          }
        } catch (jsonError) { /* ignore */ }
        setError(errorMessage);
        setPassword('');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setIsUnlocking(false);
    }
  };

  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);
  const particlesConfig = { background: { color: { value: 'transparent' } }, fpsLimit: 120, interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true }, modes: { repulse: { distance: 100, duration: 0.4 } }, }, particles: { color: { value: '#ffffff' }, links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.2, width: 1 }, move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false }, number: { density: { enable: true, area: 800 }, value: 80 }, opacity: { value: 0.3 }, shape: { type: 'circle' }, size: { value: { min: 1, max: 3 } }, }, detectRetina: true, };
  const handleCopyEmail = () => { if (!contactEmail) return; navigator.clipboard.writeText(contactEmail).then(() => setSnackbarOpen(true)); };
  const handleCloseSnackbar = () => setSnackbarOpen(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--background-color-main)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isEnabled || isUnlocked) {
    return children;
  }

  return (
    <>
      <div className="sitelock-page-wrapper">
        <Particles id="tsparticles-sitelock" init={particlesInit} options={particlesConfig} />
        <Dialog open={true} disableEscapeKeyDown PaperProps={{ className: 'sitelock-dialog-paper' }}>
          <Box component="form" onSubmit={handleSubmit}>
            <DialogTitle className="sitelock-title">Access Restricted</DialogTitle>
            <DialogContent>
              <Typography variant="body1" className="sitelock-subtitle">This site is protected and under development. Please enter the password to continue.</Typography>
              <Typography variant="body2" className="sitelock-contact-info">If you have any questions or need the password, please contact us.</Typography>
              {contactEmail && <Box className="sitelock-email-box"><Typography variant="body1" component="span" className="sitelock-email-text">{contactEmail}</Typography><IconButton onClick={handleCopyEmail} size="small" title="Copy email address"><ContentCopyIcon fontSize="small" /></IconButton></Box>}
              <TextField autoFocus required margin="dense" id="site-password" label="Password" type={showPassword ? 'text' : 'password'} fullWidth variant="outlined" value={password} onChange={(e) => setPassword(e.target.value)} error={!!error} helperText={error} InputProps={{ endAdornment: ( <InputAdornment position="end"><IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>),}}/>
            </DialogContent>
            <DialogActions className="sitelock-dialog-actions">
              <Button type="submit" variant="contained" fullWidth size="large" className="sitelock-button" disabled={isUnlocking}>{isUnlocking ? (<CircularProgress size={26} color="inherit" />) : ('Unlock')}</Button>
            </DialogActions>
          </Box>
        </Dialog>
      </div>
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }} className="sitelock-snackbar-alert">Email address copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};

export default SiteLock;